import React, { useContext, useEffect, useRef, useState } from "react"
import { FlatList, RefreshControl, View } from "react-native"
import { connect } from "react-redux"
import FilledButtonM from "@common/components/button/FilledButtonM"
import IconButtonM from "@common/components/button/IconButtonM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { schoolAssessmentsService } from "@data/services/schoolAssessmentsService"
import { theme } from "@common/theme/theme"
import TextM from "@common/components/text/TextM"
import TitleM from "@common/components/text/TitleM"
import { HeaderContainerM } from "@common/header/HeaderContainerM"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { ScreenSkeleton } from "@common/screens/ScreenSkeleton"
import { DialogM } from "@common/components/alert/dialog/Dialog"
import SchoolAssessmentCard from "./SchoolAssessmentCard"
import AddSchoolAssessmentModal from "./AddSchoolAssessmentModal"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"

const SchoolAssessmentsScreen = ({
    assessmentsByStudentId,
    schoolSubjects,
    chapters,
    studentId: propStudentId,
    student: propStudent,
    navigation,
    route,
    user,
    loading,
    noModal = false,
    width: propWidth,
    onClose,
    apiRequest,
}) => {
    // Support both props and route params (route params for navigation, props for embedded use)
    const routeStudentId = route?.params?.studentId
    const routeStudent = route?.params?.student
    const [modalVisible, setModalVisible] = useState(false)
    const [editingAssessment, setEditingAssessment] = useState(null)
    const [refreshing, setRefreshing] = useState(false)
    const lastFetchedStudentIdRef = useRef(null)

    const dimensions = useContext(DimensionsContext)

    // Determine studentId and context (props take precedence over route params)
    const studentId = propStudentId || routeStudentId || user?.id
    const isTutorContext =
        !!(propStudentId || routeStudentId) &&
        (propStudentId || routeStudentId) !== user?.id
    const targetStudent = propStudent || routeStudent || user

    // Get assessments from Redux (keyed by studentId) - undefined means never fetched
    const reduxAssessments = assessmentsByStudentId?.[studentId]
    const assessments = reduxAssessments || []

    // Check if we have data for this specific student
    const hasDataForStudent = reduxAssessments !== undefined

    // Loading state for initial load (no data yet for this student)
    const isInitialLoading = loading && !hasDataForStudent

    // Fetch assessments on mount or when studentId changes
    useEffect(() => {
        if (studentId && lastFetchedStudentIdRef.current !== studentId) {
            lastFetchedStudentIdRef.current = studentId
            fetchAssessments()
        }
    }, [studentId])

    const fetchAssessments = () => {
        apiRequest(
            schoolAssessmentsService.getSchoolAssessments,
            {},
            {},
            { student_id: studentId, upcoming: true }
        )
    }

    // Stop refreshing when API call completes
    useApiStatus(
        schoolAssessmentsService.getSchoolAssessments,
        null,
        false,
        () => setRefreshing(false),
        () => setRefreshing(false)
    )

    const onRefresh = () => {
        setRefreshing(true)
        fetchAssessments()
    }

    const handleAddPress = () => {
        setEditingAssessment(null)
        setModalVisible(true)
    }

    const handleEditPress = (assessment) => {
        setEditingAssessment(assessment)
        setModalVisible(true)
    }

    const handleDeletePress = (assessment) => {
        DialogM.show({
            text1: "Supprimer ce contrôle ?",
            text2: "Cette action est irréversible.",
            buttonText1: "Supprimer",
            buttonColor1: theme.colors.error,
            buttonText2: "Annuler",
            onPress: () => {
                apiRequest(schoolAssessmentsService.deleteSchoolAssessment, {
                    id: assessment.id,
                })
            },
        })
    }

    const handleModalClose = () => {
        setModalVisible(false)
        setEditingAssessment(null)
    }

    const handleModalSuccess = () => {
        handleModalClose()
        // Data will be updated via Redux automatically
    }

    const handleClose = () => {
        if (onClose) {
            onClose()
        } else if (navigation?.canGoBack()) {
            navigation.goBack()
        } else {
            navigation?.replace("menu")
        }
    }

    // Dynamic title based on context
    const screenTitle = isTutorContext
        ? `Contrôles de ${targetStudent?.firstname || "l'élève"}`
        : "Mes contrôles"

    // Dynamic empty state message
    const emptyStateMessage = isTutorContext
        ? `Ajoute un contrôle pour ${targetStudent?.firstname || "l'élève"}`
        : "Ajoute tes prochains contrôles pour suivre ta préparation"

    // Use prop width or dimensions width
    const contentWidth = propWidth || dimensions?.width

    const renderEmptyState = () => (
        <View
            style={{
                flex: 1,
                justifyContent: "center",
                alignItems: "center",
                paddingHorizontal: 40,
                paddingTop: 60,
            }}
        >
            <FontAwesome5
                name="calendar-check"
                size={60}
                color={theme.colors.lightMore}
            />
            <TextM
                fontSize={18}
                fontWeight="Bold"
                color={theme.colors.grey}
                style={{ marginTop: 20, textAlign: "center" }}
                wrap
            >
                Aucun contrôle à venir
            </TextM>
            <TextM
                fontSize={14}
                color={theme.colors.grey}
                style={{ marginTop: 10, textAlign: "center" }}
                wrap
            >
                {emptyStateMessage}
            </TextM>
        </View>
    )

    const content = (
        <View
            style={{
                flex: 1,
                backgroundColor: theme.colors.whiteAlmost,
                alignItems: "center",
                width: noModal ? contentWidth : undefined,
            }}
        >
            <HeaderContainerM>
                <IconButtonM
                    icon="times"
                    iconColor={theme.colors.primary}
                    size={32}
                    style={{
                        position: "absolute",
                        left: 4,
                    }}
                    onPress={handleClose}
                />
                <TitleM>{screenTitle}</TitleM>
            </HeaderContainerM>

            <FlatList
                data={assessments?.filter(Boolean) || []}
                keyExtractor={(item) => item.id.toString()}
                renderItem={({ item }) => (
                    <SchoolAssessmentCard
                        assessment={item}
                        onEdit={() => handleEditPress(item)}
                        onDelete={() => handleDeletePress(item)}
                    />
                )}
                style={{ width: contentWidth }}
                contentContainerStyle={{
                    paddingHorizontal: 15,
                    paddingTop: 15,
                    paddingBottom: 20,
                    flexGrow: 1,
                }}
                ListEmptyComponent={renderEmptyState}
                ListFooterComponent={
                    <FilledButtonM
                        mode={
                            assessments?.length > 0 ? "outlined" : "contained"
                        }
                        icon="plus"
                        color={theme.colors.primary}
                        onPress={handleAddPress}
                        style={{
                            marginTop: 10,
                            alignSelf: "center",
                        }}
                        labelStyle={{ fontSize: 12, height: 16 }}
                        contentStyle={{ height: 35 }}
                        label="Ajouter un contrôle"
                    />
                }
                refreshControl={
                    <RefreshControl
                        refreshing={refreshing}
                        onRefresh={onRefresh}
                        colors={[theme.colors.primary]}
                    />
                }
            />

            {modalVisible && (
                <AddSchoolAssessmentModal
                    visible={modalVisible}
                    onClose={handleModalClose}
                    onSuccess={handleModalSuccess}
                    assessment={editingAssessment}
                    studentId={studentId}
                    user={isTutorContext ? targetStudent : undefined}
                    schoolSubjects={schoolSubjects}
                    chapters={chapters}
                />
            )}
        </View>
    )

    // When noModal, render content directly without ScreenSkeleton wrapper
    if (noModal) {
        return content
    }

    return (
        <ScreenSkeleton isLoading={isInitialLoading}>{content}</ScreenSkeleton>
    )
}

const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        assessmentsByStudentId: state.data.schoolAssessments?.byStudentId || {},
        loading: state.api.getSchoolAssessments?.request,
        schoolSubjects: state.data.staticData?.school_subjects || [],
        chapters: state.data.chapters?.chaptersList || [],
    }
}

const mapDispatchToProps = { apiRequest, apiIdle }

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(SchoolAssessmentsScreen)
